/**
 * 书架筛选与排序纯函数模块
 */

export function filterBooks(books, state) {
  let filtered = Array.isArray(books) ? books : [];

  if (state?.shelfQuickFilter) {
    const progressOf = (b) => {
      const p = Number(b?.progress ?? 0);
      return Number.isFinite(p) ? p : 0;
    };
    if (state.shelfQuickFilter === 'reading') {
      filtered = filtered.filter(b => {
        const p = progressOf(b);
        return p > 0 && p < 100;
      });
    } else if (state.shelfQuickFilter === 'finished') {
      filtered = filtered.filter(b => progressOf(b) >= 100);
    } else if (state.shelfQuickFilter === 'planned') {
      filtered = filtered.filter(b => !!b?.plannedAt);
    }
  }

  if (state?.filterFormat && state.filterFormat !== 'all') {
    filtered = filtered.filter(book => String(book?.fileType || '') === String(state.filterFormat));
  }

  if (state?.filterCategory && state.filterCategory !== 'all') {
    if (state.filterCategory === '__uncategorized__') {
      filtered = filtered.filter(book => !book?.category);
    } else {
      filtered = filtered.filter(book => String(book?.category || '') === String(state.filterCategory));
    }
  }

  if (state?.filterCollection && state.filterCollection !== 'all') {
    const ids = Array.isArray(state.collectionMembership?.[state.filterCollection])
      ? state.collectionMembership[state.filterCollection].map(String)
      : [];
    const set = new Set(ids);
    filtered = filtered.filter(book => set.has(String(book?.id)));
  }

  const query = String(state?.searchQuery || '').trim().toLowerCase();
  if (query) {
    filtered = filtered.filter(book => {
      const title = String(book?.title || '').toLowerCase();
      const author = String(book?.author || '').toLowerCase();
      return title.includes(query) || author.includes(query);
    });
  }

  return filtered;
}

export function sortBooksForBooklist(books, orderedIds = [], sortKey = 'custom') {
  const list = [...(Array.isArray(books) ? books : [])];
  const key = sortKey || 'custom';

  if (key === 'custom') {
    const orderMap = new Map((Array.isArray(orderedIds) ? orderedIds : []).map((id, i) => [String(id), i]));
    list.sort((a, b) => {
      const ai = orderMap.has(String(a?.id)) ? orderMap.get(String(a?.id)) : Infinity;
      const bi = orderMap.has(String(b?.id)) ? orderMap.get(String(b?.id)) : Infinity;
      if (ai !== bi) return ai - bi;
      const byAddedAt = new Date(b?.addedAt || 0) - new Date(a?.addedAt || 0);
      if (byAddedAt !== 0) return byAddedAt;
      return String(a?.id || '').localeCompare(String(b?.id || ''));
    });
    return list;
  }

  if (key === 'addedAt') {
    list.sort((a, b) => new Date(b?.addedAt || 0) - new Date(a?.addedAt || 0));
    return list;
  }

  if (key === 'lastReadAt') {
    list.sort((a, b) => {
      if (!a?.lastReadAt) return 1;
      if (!b?.lastReadAt) return -1;
      return new Date(b.lastReadAt) - new Date(a.lastReadAt);
    });
    return list;
  }

  if (key === 'title') {
    list.sort((a, b) => String(a?.title || '').localeCompare(String(b?.title || ''), 'zh-CN'));
    return list;
  }

  if (key === 'progress') {
    const p = (b) => {
      const v = Number(b?.progress ?? 0);
      return Number.isFinite(v) ? v : 0;
    };
    list.sort((a, b) => p(b) - p(a));
    return list;
  }

  return list;
}

export function sortBooklists(lists, sortKey = 'custom', state) {
  const arr = [...(Array.isArray(lists) ? lists : [])];
  const key = sortKey || 'custom';

  if (key === 'custom') {
    const orderMap = new Map((state?.booklistOrder || []).map((id, i) => [String(id), i]));
    arr.sort((a, b) => {
      const ai = orderMap.has(String(a?.id)) ? orderMap.get(String(a?.id)) : Infinity;
      const bi = orderMap.has(String(b?.id)) ? orderMap.get(String(b?.id)) : Infinity;
      if (ai !== bi) return ai - bi;
      const byCreated = new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0);
      if (byCreated !== 0) return byCreated;
      return String(a?.id || '').localeCompare(String(b?.id || ''));
    });
    return arr;
  }

  if (key === 'title') {
    arr.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'zh-CN'));
    return arr;
  }

  const membership = state?.booklistMembership || {};
  const bookMap = new Map((state?.books || []).map(b => [String(b.id), b]));

  if (key === 'addedAt') {
    arr.sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0));
    return arr;
  }

  if (key === 'lastReadAt') {
    const lastReadOf = (list) => {
      const ids = Array.isArray(membership?.[list?.id]) ? membership[list.id].map(String) : [];
      return Math.max(
        0,
        ...ids.map(id => {
          const bk = bookMap.get(id);
          return bk?.lastReadAt ? new Date(bk.lastReadAt).getTime() : 0;
        })
      );
    };
    arr.sort((a, b) => lastReadOf(b) - lastReadOf(a));
    return arr;
  }

  if (key === 'progress') {
    const avgProgressOf = (list) => {
      const ids = Array.isArray(membership?.[list?.id]) ? membership[list.id].map(String) : [];
      const books = ids.map(id => bookMap.get(id)).filter(Boolean);
      if (books.length === 0) return -Infinity;
      const sum = books.reduce((acc, bk) => {
        const v = Number(bk?.progress ?? 0);
        return acc + (Number.isFinite(v) ? v : 0);
      }, 0);
      return sum / books.length;
    };
    arr.sort((a, b) => avgProgressOf(b) - avgProgressOf(a));
    return arr;
  }

  return arr;
}

export function sortBooks(books, state) {
  const sorted = [...(Array.isArray(books) ? books : [])];

  switch (state?.sortBy) {
    case 'addedAt':
      sorted.sort((a, b) => new Date(b?.addedAt || 0) - new Date(a?.addedAt || 0));
      break;
    case 'lastReadAt':
      sorted.sort((a, b) => {
        if (!a?.lastReadAt) return 1;
        if (!b?.lastReadAt) return -1;
        return new Date(b.lastReadAt) - new Date(a.lastReadAt);
      });
      break;
    case 'title':
      sorted.sort((a, b) => String(a?.title || '').localeCompare(String(b?.title || ''), 'zh-CN'));
      break;
    case 'progress':
      sorted.sort((a, b) => {
        const av = Number(a?.progress ?? 0);
        const bv = Number(b?.progress ?? 0);
        return (Number.isFinite(bv) ? bv : 0) - (Number.isFinite(av) ? av : 0);
      });
      break;
    case 'custom':
      sorted.sort((a, b) => {
        const orderA = typeof a?.sortOrder === 'number' && Number.isFinite(a.sortOrder) ? a.sortOrder : Infinity;
        const orderB = typeof b?.sortOrder === 'number' && Number.isFinite(b.sortOrder) ? b.sortOrder : Infinity;
        const byOrder = orderA - orderB;
        if (byOrder !== 0) return byOrder;

        const byAddedAt = new Date(b?.addedAt || 0) - new Date(a?.addedAt || 0);
        if (byAddedAt !== 0) return byAddedAt;
        return String(a?.id || '').localeCompare(String(b?.id || ''));
      });
      break;
  }

  return sorted;
}

export function sortCollections(collections, state) {
  const membership = state?.collectionMembership || {};
  const bookMap = new Map((state?.books || []).map(b => [String(b.id), b]));
  const arr = [...(Array.isArray(collections) ? collections : [])];
  switch (state?.sortBy) {
    case 'addedAt':
      arr.sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0));
      break;
    case 'lastReadAt':
      arr.sort((a, b) => {
        const idsA = Array.isArray(membership?.[a?.id]) ? membership[a.id].map(String) : [];
        const idsB = Array.isArray(membership?.[b?.id]) ? membership[b.id].map(String) : [];
        const lastA = Math.max(
          0,
          ...idsA.map(id => {
            const bk = bookMap.get(id);
            return bk?.lastReadAt ? new Date(bk.lastReadAt).getTime() : 0;
          })
        );
        const lastB = Math.max(
          0,
          ...idsB.map(id => {
            const bk = bookMap.get(id);
            return bk?.lastReadAt ? new Date(bk.lastReadAt).getTime() : 0;
          })
        );
        return lastB - lastA;
      });
      break;
    case 'title':
      arr.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'zh-CN'));
      break;
    case 'progress':
      arr.sort((a, b) => {
        const avg = (ids) => {
          const list = ids.map(id => bookMap.get(id)).filter(Boolean);
          if (list.length === 0) return -Infinity;
          const sum = list.reduce((acc, bk) => acc + (Number(bk?.progress ?? 0) || 0), 0);
          return sum / list.length;
        };
        const idsA = Array.isArray(membership?.[a?.id]) ? membership[a.id].map(String) : [];
        const idsB = Array.isArray(membership?.[b?.id]) ? membership[b.id].map(String) : [];
        return avg(idsB) - avg(idsA);
      });
      break;
    case 'custom': {
      const byGlobal = new Map((state?.globalShelfOrder || []).map((k, i) => [k, i]));
      const byIndex = new Map((state?.collections || []).filter(Boolean).map((c, i) => [String(c.id), i]));
      arr.sort((a, b) => {
        const aKey = `collection:${a?.id}`;
        const bKey = `collection:${b?.id}`;
        const ai = byGlobal.has(aKey) ? byGlobal.get(aKey) : Infinity;
        const bi = byGlobal.has(bKey) ? byGlobal.get(bKey) : Infinity;
        if (ai !== bi) return ai - bi;
        const aIdx = byIndex.has(String(a?.id)) ? byIndex.get(String(a?.id)) : Infinity;
        const bIdx = byIndex.has(String(b?.id)) ? byIndex.get(String(b?.id)) : Infinity;
        if (aIdx !== bIdx) return aIdx - bIdx;
        return new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0);
      });
      break;
    }
  }
  return arr;
}

export function getFilteredCollectionsForShelf(state) {
  const collections = Array.isArray(state?.collections) ? state.collections : [];
  const query = String(state?.searchQuery || '').toLowerCase();
  const membership = state?.collectionMembership || {};
  const bookMap = new Map((state?.books || []).map(b => [String(b.id), b]));

  const filtered = query
    ? collections.filter(c => {
      const nameMatch = String(c?.name || '').toLowerCase().includes(query);
      if (nameMatch) return true;
      const ids = Array.isArray(membership?.[c?.id]) ? membership[c.id].map(String) : [];
      for (const id of ids) {
        const b = bookMap.get(id);
        if (!b) continue;
        const title = String(b?.title || '').toLowerCase();
        const author = String(b?.author || '').toLowerCase();
        if (title.includes(query) || author.includes(query)) return true;
      }
      return false;
    })
    : collections;

  return sortCollections(filtered, state);
}
